import type { Tv2mLinkContext } from '@apm/contracts';
import type { CheckoutDeps, CheckoutTracking } from '../checkout.js';
import { ProductPage } from '../pages/ProductPage.js';
import './MiniPlayer.css';
import { useT } from '../i18n/LocaleProvider.js';
import type { SheetNote } from '../handoff.js';

/**
 * 상품 시트 — **Tier 0 의 도착지다** (절대 규칙 14).
 *
 * ── 무엇이 바뀌었는가 ───────────────────────────────────────
 *
 * v7 은 여기서 `location.href` 로 다른 오리진(모바일 웹)의 상품 화면으로 갔고, 그때
 * 재생이 끝났다. B7 이 두 앱을 한 오리진으로 합친 뒤로는 **같은 화면 위에 겹치기만 하면
 * 된다** — 영상은 위로 축소된 채 계속 간다 (`MiniPlayer.css`).
 *
 * ── 왜 `ProductPage` 를 그대로 쓰는가 ────────────────────────
 *
 * 딥링크로 온 사람과 같은 기기에서 누른 사람이 **다른 상품 화면을 볼 이유가 없다.**
 * 화면을 두 벌로 만들면 B10 이 `[판매처로 이동]` 을 붙일 때 고칠 곳이 둘이 되고, 한쪽만
 * 고쳐졌을 때의 증상은 「어떤 경로로 들어왔느냐에 따라 구매 버튼이 다르다」라 재현하기
 * 어렵다 (`docs/14` B9 — "상품 화면과 같은 컴포넌트를 시트로 감싼다").
 *
 * **B10 이 그 판단을 실제로 시험했다.** `[판매처로 이동]` 을 붙일 곳이 한 곳뿐이었고,
 * 두 벌이었다면 「어떤 경로로 들어왔느냐에 따라 구매 버튼이 다르다」가 됐을 것이다.
 *
 * 대가로 **이미 아는 상품을 한 번 더 조회한다.** 시청 화면의 `PlaylistItem` 에 제목·가격·
 * 썸네일이 다 있는데 `ProductPage` 는 `productId` 로 BLOOM Server 를 다시 부른다. 받아들인 이유는
 * 그 조회가 시트의 조건이 아니기 때문이다 — 실패해도 시트는 뜬다. **다만 B10 부터는
 * 그 조회에서 판매처 주소가 오므로**, 실패하면 나갈 곳이 없어 구매 버튼이 비활성이다
 * (`ProductPage` 의 "판매처 주소는 상품 조회에만 있다").
 */
export function ProductSheet({
  context,
  note,
  checkout,
  tracking,
  tv2mServerUrl,
  onClose,
}: {
  /** 이 시트가 여는 상품. `matchId` 는 서버 응답이 도착한 뒤에 채워진다 */
  context: Tv2mLinkContext;
  /** 응답이 늦거나 예상과 달랐을 때의 한 줄. **시트를 닫지 않는다** (`docs/14` B9 의 5번) */
  note?: SheetNote | undefined;
  checkout: CheckoutDeps;
  /** 시청 세션이 값을 들고 있다. 이 경로에서는 둘 다 채워져 있다 (`checkout.ts`) */
  tracking: CheckoutTracking;
  /**
   * `ProductPage` 가 광고 소재를 읽을 곳 (B11).
   *
   * ★ **v21 부터 이 시트에서도 쓰인다.** 전에는 「시청 화면의 트리거는 언제나
   * `channel_content`」라고 적혀 있었고 그것이 v16 까지는 참이었다 — **광고 중의 구매가
   * 그 전제를 깼다**(절대 규칙 45 의 개정). 무엇을 여는지는 `context.triggerType` 이
   * 말하고, 그 값을 만드는 것은 `sheetLinkContext` 다.
   *
   * 화면을 한 벌로 둔 값어치가 여기서 드러난다 — 광고가 들어오는 데 **이 컴포넌트는 한
   * 줄도 바뀌지 않았다.**
   */
  tv2mServerUrl: string;
  onClose: () => void;
}) {
  const t = useT();
  return (
    /*
      `data-focus-scope` — 리모컨 포커스를 이 시트 안에 가둔다 (절대 규칙 32).

      **TV 계열에서는 이 시트가 열리지 않는다** — `handoff.kind === 'sheet'` 는
      `clientType === 'mobile_web'` 일 때만 서고, 그 유형에서는 리모컨이 꺼져 있다
      (`isRemoteControlled`). 완결성으로 붙여 둔다 — 훗날 이 조건이 넓어지는 날
      **빠뜨렸다는 것을 알아차릴 방법이 화면에 없다.**
    */
    <div className="product-sheet" role="dialog" aria-label={t('product.title')} data-focus-scope>
      <div className="product-sheet__panel">
        <div className="product-sheet__head">
          <h2 className="sheet__title">{t('product.title')}</h2>
          {/*
            **닫으면 전체화면으로 돌아온다.** 재생은 멈춘 적이 없으므로 되돌릴 것이
            화면 크기뿐이다 — 그것이 이 시트가 페이지 이동이 아닌 이유 전부다.
          */}
          <button type="button" className="product-sheet__close" data-focus-back onClick={onClose}>
            {t('product.continueWatching')}
          </button>
        </div>

        {note !== undefined && (
          <p className="product-sheet__note">{t(note.key, { n: note.count ?? 0 })}</p>
        )}

        <ProductPage
          context={context}
          checkout={checkout}
          tracking={tracking}
          tv2mServerUrl={tv2mServerUrl}
          // 머리의 닫기와 같은 동작이다. 시트가 길어지면 위쪽 버튼이 화면 밖으로 밀리는데,
          // **시청을 이어가는 것이 기본 동작**이므로 바닥에도 자리가 있어야 한다.
          onContinue={onClose}
        />
      </div>
    </div>
  );
}
